import { lazy, Suspense, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import { useAuthState, useCrossDeviceUploads, usePackagesActions } from "@/store";
import { visibleRemoteUploads } from "./shouldFastPollFromState";

// Lazy so this user-facing card doesn't pay for the admin-only drawer's bundle
// on every render for non-admins (drawer pulls in the whole admin diagnostics
// tree). Loaded only when an admin clicks the bug icon on this card.
const AdminUploadDiagnosticsDrawer = lazy(() =>
  import("@/pages/Admin/AdminUploadDiagnosticsDrawer")
);

// Read-only spectator card for uploads the contributor has running (or paused) on another
// device / browser. Mirrors the current multi-tab peer view: a live progress bar driven by
// backend state (refreshed by CrossDeviceUploadsWatcher every ~30s + on visibility wake) plus
// a "Take over here" button that transfers ownership to this browser.
//
// The card intentionally has no Pause / Resume / Cancel controls — those would race the
// device that actually owns the bytes. Once the user takes over, they can drive the upload
// via the normal resume flow.
//
// PAUSED uploads on another device get a distinct amber accent + prominent status chip so a
// user browsing "Active uploads" from a fresh device immediately recognises the state — it's
// the number-one thing they'd want to see and act on ("did the upload from my other laptop
// finish, or is it stuck?").

const PAUSED_ACCENT = {
  bg: "rgba(255, 176, 46, 0.10)",
  border: "rgba(255, 176, 46, 0.55)",
  chipBg: "rgba(255, 176, 46, 0.22)",
  chipFg: "#FFCB70",
  progressBar: "#FFB02E",
};

const UPLOADING_ACCENT = {
  bg: "rgba(41,56,77,0.55)",
  border: "rgba(139,148,255,0.32)",
  chipBg: "rgba(99,102,199,0.24)",
  chipFg: "#B4B8FF",
  progressBar: "#8B94FF",
};

const chipLabel = (status) => {
  switch (status) {
    case "PAUSED":
      return "Paused · another device";
    case "PENDING_CHUNKS":
      return "Waiting · another device";
    case "CHUNK_UPLOAD_IN_PROGRESS":
      return "Uploading · another device";
    case "CHUNK_UPLOAD_ASSEMBLING":
    case "READY_TO_UPLOAD_TO_PROVIDER":
    case "IN_PROGRESS_UPLOAD_TO_PROVIDER":
    case "READY_FOR_CAT_DV":
      return "Finalizing on server";
    default:
      return (status || "In progress").replace(/_/g, " ");
  }
};

const helperText = (status) => {
  switch (status) {
    case "PAUSED":
      return "This upload was paused on another device. Take it over here to resume.";
    case "CHUNK_UPLOAD_IN_PROGRESS":
      return "Bytes are flowing from another device or browser session.";
    case "PENDING_CHUNKS":
      return "Another device initialised this upload but hasn't started sending bytes yet.";
    default:
      return null;
  }
};

const percent = (bytesUploaded, totalSize) => {
  if (!totalSize || totalSize <= 0) return 0;
  const p = Math.min(100, Math.round(((bytesUploaded || 0) / totalSize) * 100));
  return isFinite(p) ? p : 0;
};

export const CrossDeviceUploadCard = ({ upload }) => {
  const progress = percent(upload.bytesUploaded, upload.totalSize);
  const isPaused = upload.status === "PAUSED";
  const accent = isPaused ? PAUSED_ACCENT : UPLOADING_ACCENT;
  const description = helperText(upload.status);
  // Admin-only escape hatch: an admin browsing their own cross-device uploads
  // (or a colleague's, once impersonation lands) can jump straight to the
  // PostHog session replay + captured client diagnostics without having to
  // switch to the AdminUploadsLivePage and re-find the row.
  const { currentUser } = useAuthState();
  const isAdmin = currentUser?.role === "admin";
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        borderRadius: 2,
        backgroundColor: accent.bg,
        border: `1px solid ${accent.border}`,
        boxShadow: isPaused ? `0 0 0 2px ${accent.border} inset` : "none",
      }}
      data-testid="cross-device-upload-card"
      data-upload-id={upload.uploadId}
      data-upload-status={upload.status}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={chipLabel(upload.status)}
              size="small"
              sx={{
                backgroundColor: accent.chipBg,
                color: accent.chipFg,
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                height: 22,
                borderRadius: "6px",
              }}
            />
          </Stack>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              color: "rgba(247,250,250,0.98)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={upload.packageName || "Upload"}
          >
            {upload.packageName || "Upload"}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: 12, color: "rgba(247,250,250,0.68)", mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        {isAdmin && (
          <Tooltip title="Open diagnostics">
            <IconButton
              size="small"
              onClick={() => setDiagnosticsOpen(true)}
              data-testid={`cross-device-diagnostics-open-${upload.uploadId}`}
              aria-label={`open diagnostics for ${upload.uploadId}`}
              sx={{
                color: "rgba(247,250,250,0.82)",
                border: "1px solid rgba(255,255,255,0.14)",
                "&:hover": { background: "rgba(255,255,255,0.08)" },
              }}
            >
              <BugReportOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.08)",
          "& .MuiLinearProgress-bar": { backgroundColor: accent.progressBar },
        }}
      />
      <Typography sx={{ fontSize: 11, color: "rgba(247,250,250,0.6)", mt: 0.5 }}>
        {progress}% · {upload.filesCount ? `${upload.filesCount} file${upload.filesCount === 1 ? "" : "s"}` : "files"}
      </Typography>
      {isAdmin && diagnosticsOpen && (
        <Suspense fallback={null}>
          <AdminUploadDiagnosticsDrawer
            open
            uploadId={upload.uploadId}
            initialRow={upload}
            onClose={() => setDiagnosticsOpen(false)}
          />
        </Suspense>
      )}
    </Box>
  );
};

export const CrossDeviceUploadsList = () => {
  const { items } = useCrossDeviceUploads();
  const { claimAllVisibleUploads } = usePackagesActions();
  // Local because the bulk claim thunk (N × metadata fetch + IDB write + rehydrate) takes
  // seconds when the contributor has multiple in-flight uploads. Spinner covers the whole
  // duration so users see immediate feedback on the click.
  const [isClaiming, setIsClaiming] = useState(false);

  // Hide rows that have already left the byte-transfer phase — see visibleRemoteUploads for
  // the rationale (the "100% finalizing card sits for 1 minute" bug from browser 1 after
  // browser 2 completes a cross-device takeover).
  const remote = visibleRemoteUploads(items);
  if (remote.length === 0) return null;

  const onTakeOverAll = async () => {
    if (isClaiming) return;
    setIsClaiming(true);
    try {
      await claimAllVisibleUploads();
    } finally {
      setIsClaiming(false);
    }
  };

  const buttonLabel = isClaiming
    ? "Taking over…"
    : (remote.length === 1 ? "Take over here" : `Take over here (${remote.length})`);

  return (
    <Stack spacing={2} data-testid="cross-device-uploads-section" sx={{ pt: 2, pb: 1, width: "100%", maxWidth: 880, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.045em", textTransform: "uppercase", color: "rgba(247,250,250,0.65)" }}>
          On other devices
        </Typography>
        {/* Section-level Take Over: makes THIS browser the main device for this contributor
            by claiming every visible remote upload at once. Matches the single-active-upload-
            per-contributor invariant — cross-device visibility is "see what other devices
            are doing", so ownership migration is naturally an all-or-nothing gesture. */}
        <Button
          variant="contained"
          size="small"
          onClick={onTakeOverAll}
          disabled={isClaiming}
          startIcon={isClaiming ? <CircularProgress size={14} color="inherit" /> : null}
          data-testid="cross-device-take-over-all-btn"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            whiteSpace: "nowrap",
            backgroundColor: "#6366C7",
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#7174D8",
              boxShadow: "none",
            },
          }}
        >
          {buttonLabel}
        </Button>
      </Stack>
      {remote.map((upload) => (
        <CrossDeviceUploadCard key={upload.uploadId} upload={upload} />
      ))}
    </Stack>
  );
};

export default CrossDeviceUploadCard;
